import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Tab, Tabs } from "@mui/material";
import SlideshowOutlined from "@mui/icons-material/SlideshowOutlined";
import "../../assets/css/Text-General.css";
import "../../assets/css/tabs.css";
import "../../assets/css/summary.css";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { MenuDropDown } from "../../components/common/MenuDropDown";
import { useTenantFeatures } from "../../context/TenantFeaturesContext";
import { exportTenFiftyPptSummary } from "../../services/powerpoint/exportTenFiftyPptSummary";
import { ChartsTab } from "./summary/ChartsTab";
import { DealAnalysisTab } from "./summary/DealAnalysisTab";
import { ClosedDealsTab } from "./summary/ClosedDealsTab";
import { KilledDealsTab } from "./summary/KilledDealsTab";
import { LostDealsTab } from "./summary/LostDealsTab";
import { StatusAnalysisTab } from "./summary/StatusAnalysisTab.tsx";
import { ClosingTrackerTab } from "./summary/ClosingTrackerTab";
import { DealFunnelTab } from "./summary/DealFunnelTab";
import { SponsorsTab } from "./summary/SponsorsTab";
import { UserAllocationsTab } from "./summary/UserAllocationsTab";
import { AdvisorFeesTab } from "./summary/AdvisorFeesTab";

const BASE_TAB_CONFIG = [
  { id: "charts", label: "Charts" },
  { id: "deal-analysis", label: "Deal Volume" },
  { id: "closed-deals", label: "Closed" },
  { id: "status-analysis", label: "Status Analysis" },
  { id: "closing-tracker", label: "Closing Tracker" },
  { id: "deal-funnel", label: "Deal Funnel" },
  { id: "killed-deals", label: "Killed Deals" },
  { id: "lost-deals", label: "Lost Deals" },
  { id: "sponsors", label: "Sponsors" },
  { id: "user-allocations", label: "User Allocations" },
] as const;

const ADVISOR_FEES_TAB = {
  id: "advisor-fees",
  label: "Advisor Fees",
} as const;

type SummaryTabId =
  | (typeof BASE_TAB_CONFIG)[number]["id"]
  | typeof ADVISOR_FEES_TAB.id;

function TabPanel(props: {
  children: ReactNode;
  value: number;
  index: number;
}) {
  const { children, value, index } = props;
  if (value !== index) return null;
  return <div className="summary-tab-content">{children}</div>;
}

const Summary = () => {
  const { isFeatureEnabled } = useTenantFeatures();
  const showAdvisorFees = isFeatureEnabled("deal_advisor_fees");
  const tabConfig = useMemo(
    () =>
      showAdvisorFees
        ? [...BASE_TAB_CONFIG, ADVISOR_FEES_TAB]
        : [...BASE_TAB_CONFIG],
    [showAdvisorFees],
  );

  const tabIndexFromParam = (tabParam: string | null): number => {
    if (!tabParam) return 0;
    const idx = tabConfig.findIndex((tab) => tab.id === tabParam);
    return idx >= 0 ? idx : 0;
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(() => tabIndexFromParam(tabParam));
  const [isExportingPpt, setIsExportingPpt] = useState(false);

  useEffect(() => {
    setActiveTab(tabIndexFromParam(tabParam));
  }, [tabParam, tabConfig]);

  const handleTabChange = (_: unknown, next: number) => {
    setActiveTab(next);
    const tabId = tabConfig[next]?.id as SummaryTabId | undefined;
    if (!tabId || tabId === "charts") {
      // Charts is the default — drop the query param
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.delete("tab");
          return nextParams;
        },
        { replace: true },
      );
      return;
    }
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set("tab", tabId);
        return nextParams;
      },
      { replace: true },
    );
  };

  const handleExportTenFiftyPptSummary = async () => {
    if (isExportingPpt) return;
    setIsExportingPpt(true);
    try {
      await exportTenFiftyPptSummary();
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Failed to export TenFifty PPT Summary. Please try again.",
      );
    } finally {
      setIsExportingPpt(false);
    }
  };

  return (
    <div className="summary-page">
      <Box
        sx={{
          padding: "0px",
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "background.paper",
          boxShadow: "0px 1px 5px 0px rgba(44, 62, 80, 0.12)",
          "& .toolbar": {
            top: 0,
            alignItems: "center",
            flexWrap: "nowrap",
            minWidth: 0,
            maxWidth: "100%",
          },
          "& .toolbar-left": {
            flex: "1 1 10%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            padding: "4px 8px",
            flexWrap: "nowrap",
            alignItems: "center",
          },
          "& .toolbar-right": {
            flex: "0 0 auto",
            width: "auto",
            maxWidth: "none",
            marginLeft: "0 !important",
            flexShrink: 0,
            justifyContent: "flex-end",
            paddingRight: "15px",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        <SimpleToolbar customToolbarPadding="0px 0px">
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              alignSelf: "center",
              display: "flex",
              padding: 0,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{ className: "tf-tabs__indicator" }}
              sx={{
                width: "100%",
                minWidth: 0,
                minHeight: 0,
                "& .MuiTabs-flexContainer": { alignItems: "center" },
                "& .MuiTabScrollButton-root": { width: 15 },
                "& .MuiTab-root.tf-tab.tf-tab--compact": {
                  minHeight: "43px !important",
                  py: 1,
                },
              }}
            >
              {tabConfig.map((t) => (
                <Tab
                  key={t.id}
                  className="tf-tab tf-tab--compact"
                  label={t.label}
                />
              ))}
            </Tabs>
          </Box>
          <Box
            data-position="right"
            sx={{
              display: "flex",
              alignItems: "center",
              "& .MuiIconButton-root": { p: 0.6 },
              "& .MuiIconButton-root .MuiSvgIcon-root": { fontSize: 22 },
            }}
          >
            <MenuDropDown
              title="Summary Menu"
              tooltip="Menu"
              menuItems={[
                {
                  label: isExportingPpt
                    ? "Exporting PPT Summary..."
                    : "Export TenFifty PPT Summary",
                  icon: <SlideshowOutlined fontSize="small" />,
                  onClick: () => {
                    void handleExportTenFiftyPptSummary();
                  },
                },
              ]}
            />
          </Box>
        </SimpleToolbar>
      </Box>

      <div className="summary-tab-panel">
        <TabPanel value={activeTab} index={0}>
          <ChartsTab />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <DealAnalysisTab statusIds={[]} statusFilter={[]} />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <ClosedDealsTab />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <StatusAnalysisTab />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <ClosingTrackerTab />
        </TabPanel>
        <TabPanel value={activeTab} index={5}>
          <DealFunnelTab />
        </TabPanel>
        <TabPanel value={activeTab} index={6}>
          <KilledDealsTab />
        </TabPanel>
        <TabPanel value={activeTab} index={7}>
          <LostDealsTab />
        </TabPanel>
        <TabPanel value={activeTab} index={8}>
          <SponsorsTab />
        </TabPanel>
        <TabPanel value={activeTab} index={9}>
          <UserAllocationsTab />
        </TabPanel>
        {showAdvisorFees ? (
          <TabPanel value={activeTab} index={10}>
            <AdvisorFeesTab />
          </TabPanel>
        ) : null}
      </div>
    </div>
  );
};

export default Summary;
